import { KaseApi } from '@/api';
import { IAddress, IOffense } from '@/models';
import { ConfirmTypes, useToastStore } from '@/store';

interface UseOffenseSyncProps {
  kaseId: string;
}

interface UseOffenseSyncReturn {
  syncArrestChargesToOffenses: (arrestCharges: any[]) => Promise<void>;
}

export const useOffenseSync = ({
  kaseId,
}: UseOffenseSyncProps): UseOffenseSyncReturn => {
  const { updateToast } = useToastStore();

  const syncArrestChargesToOffenses = async (arrestCharges: any[]) => {
    if (!kaseId || !arrestCharges || arrestCharges.length === 0) {
      return;
    }

    try {
      // Fetch current offenses to get their IDs
      const { data } = await KaseApi.getOffenses(kaseId, { limit: 1000 });

      // Build a map of penalCodeId -> offense
      const offensesMap = new Map<string, IOffense>();
      data.results.forEach((offense) => {
        if (offense.penalCode?._id) {
          offensesMap.set(offense?.penalCode?._id, offense);
        }
      });

      // Update each offense that matches an arrest charge
      const updatePromises = arrestCharges
        .filter((charge) => charge?.penalCode) // Only process charges with penal codes
        .map(async (charge) => {
          const penalCodeId =
            typeof charge?.penalCode === 'string'
              ? charge?.penalCode
              : charge?.penalCode?._id?.toString();

          if (!penalCodeId) return;

          const matchingOffense = offensesMap.get(penalCodeId);
          if (!matchingOffense || !matchingOffense._id) return;

          // Prepare update payload
          const updatePayload: {
            number?: number;
            penalCode: any;
            kase?: string;
            ibrCode?: string;
            offenderVictims: {
              offender: string;
              victims: { victim: string; relationship: string }[];
            }[];
            startDatetime: string;
            endDatetime?: string | null;
            timeZone: string;
            deleted?: boolean;
            deletedAt?: Date;
            _id: string;
            readonly createdAt: Date;
            readonly updatedAt: Date;
            attemptIndicator: any;
            count: any;
            larcenyType?: string | null;
            locationType?: string | null;
            offenseLocation?: IAddress | null;
            weaponForces: string[];
            numberOfPremisesEntered?: string | null;
            methodOfEntry?: string | null;
            assaultHomicideCircumstances: string[];
            justifiableHomicideCircumstance?: string | null;
            offenderUsedComputerOrHandheldDevice?: string | null;
            criminalActivityGangInformationTypes: string[];
            bond?: number;
            offenseStartDate?: string;
            offenseStartTime?: string;
            offenseEndDate?: string;
            offenseEndTime?: string;
          } = {
            ...matchingOffense,
            _id: matchingOffense._id,
            penalCode: penalCodeId,
            attemptIndicator: charge.attemptIndicator,
            count: charge.count,
            offenderVictims: matchingOffense.offenderVictims.map((ov) => ({
              offender:
                typeof ov.offender === 'string'
                  ? ov.offender
                  : ov.offender._id?.toString() || '',
              victims: ov.victims.map((v) => ({
                victim:
                  typeof v.victim === 'string'
                    ? v.victim
                    : v.victim._id?.toString() || '',
                relationship: v.relationship,
              })),
            })),
            offenseLocation: matchingOffense.offenseLocation || null,
          };

          // Update the offense
          await KaseApi.updateOffense(kaseId, {
            ...updatePayload,
            offenseLocation:
              updatePayload.offenseLocation?._id?.toString() ?? null,
          });
        });

      await Promise.all(updatePromises);
    } catch (err: any) {
      // Don't fail the entire save operation if offense sync fails
      updateToast({
        type: ConfirmTypes.WARNING,
        open: true,
        message: 'Arrestee saved but some offense updates may have failed',
      });
    }
  };

  return {
    syncArrestChargesToOffenses,
  };
};
